import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Col, Radio, Row, Switch } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import Spinner from 'common/spinner/components/spinner';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Button, Dropdown, Flex, notification, TimePicker, Icon as UIIcon } from 'ui';
import { publishSteps } from 'utils/app-configs';
import moment from 'utils/cops-moment';
import useMultiStatusPoll from 'utils/useMultiStatusPoll';
import { withRouter } from 'utils/withRouter';
import TypesMenu from '../../containers/types-menu-container';
import settingsConfig from '../../settings-config';
// eslint-disable-next-line
import ServiceCenterToggle from '../../../header/service-center-toggle/containers/service-center-toggle-container';

const StyledDiv = styled.div`
  margin-top: 10px;
`;

const SuccessIcon = styled(CheckCircleOutlined)`
  color: ${p => p.theme.colors.publishSteps.finish}
`;

const Warning = styled.div`
  margin: 20px 0;
  text-transform: uppercase;
  width: 70%;
`;

const StyledWarning = styled(Flex)``;


const WarningText = styled.span`
  margin-left: 5px;
  color: ${p => p.theme.colors.theme};
`;

const WarningItem = styled.li`
  list-style-type: disc;
  color: ${p => p.theme.colors.theme};
`;

const WarningList = styled.ul`
  margin-left: 60px;
`;

const WarningIcon = styled(UIIcon)`
  line-height: 25px;
`;

const ErrorIcon = styled(UIIcon)`
  line-height: 30px;
  color: ${p => p.theme.colors.alerts[1]}
`;

const LoadingIcon = styled(LoadingOutlined)`
  line-height: 30px;
  color: ${p => p.theme.colors.theme}
`;


const StyledGrid = styled.div`
  margin-top: 20px;
  width: 1100px;
`;

const StyledSaveButton = styled(Button)`
  text-transform: uppercase;
  margin-top: 30px;
`;

const UndoButton = styled(Button)`
  text-transform: uppercase;
  margin-top: 30px;
  margin-right: 20px;
`;

const StyledRow = styled(Row)`
  margin-top: 10px;
  align-items: center;
`;

const StyledDropdown = styled(Dropdown)`
  width: 250px;
  margin-right: 60px;
`;

const RestoreButton = styled(Button)`
  text-transform: uppercase;
  margin-left: 20px;
  width: 140px;
`;

const DropdownFlex = styled(Flex)`
`;

const StyledLabel = styled.p`
  font-size: 14px;
`;

const StyledTimePicker = styled(TimePicker)`
  width: 125px !important;
`;

const Status = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const StatusText = styled.p`
  margin-left: 10px;
`;

// eslint-disable-next-line complexity
const WorkingHours = ({
  match: {
    params: {
      type,
      plant,
      region,
      fse,
    },
  },
  history,
  fetchPlants,
  isLoadingPlants,
  plants,
  serviceCenterId,
  intl,
  data,
  fetchWorkingHours,
  error,
  updateWorkingHours,
  isLoading,
  updateSuccess,
  setDefaultWorkingHours,
  isFetching,
  restoreWorkingHours,
  isRestoring,
}) => {
  const navigate = useNavigate();
  const [prevServiceCenterId, setPrevServiceCenterId] = useState();
  const [regions, setRegions] = useState([]);
  const [fses, setFses] = useState([]);
  const [plantValue, setPlantValue] = useState();
  const [regionValue, setRegionValue] = useState();
  const [fseValue, setFseValue] = useState();
  const [prevType, setPrevType] = useState();
  const [dataByDay, setDataByDay] = useState({});
  const [initialData, setInitialData] = useState({});
  const [fromMessageCenter, setFromMessageCenter] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [prevMultiStatusInfo, setPrevMultiStatusInfo] = useState();
  const [successIconIds, setSuccessIconIds] = useState([]);
  const loading = isLoadingPlants || isLoading;
  const isPlant = type === settingsConfig.types.plant;
  const isRegion = type === settingsConfig.types.region;
  const isFSE = type === settingsConfig.types.fse;

  const refetchWorkingHours = () => {
    let id = '';
    if (isPlant) {
      id = plantValue;
    } else if (isRegion) {
      id = regionValue;
    } else if (isFSE) {
      id = fseValue;
    }
    if (id) {
      fetchWorkingHours({
        id,
        hideLoadingSpinner: true,
      });
    }
  };
  const [multiStatusInfo] = useMultiStatusPoll(data?.items?.filter(i => i.id), refetchWorkingHours);

  const checkIfDirty = () => {
    if (!isEqual(initialData, dataByDay)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  };

  useEffect(() => {
    if (!type) {
      navigate(`${settingsConfig.types.plant}`);
    }
  }, [type]);

  useEffect(() => {
    if (
      multiStatusInfo &&
      Object.keys(multiStatusInfo).length &&
      !isEqual(prevMultiStatusInfo, multiStatusInfo)
    ) {
      setPrevMultiStatusInfo(multiStatusInfo);
    }
  }, [multiStatusInfo]);

  useEffect(() => {
    if (updateSuccess) {
      notification.success({
        message: intl.formatMessage({
          id: 'workingHours.UPDATE_SUCCESS',
        }),
        placement: 'top',
      });
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (data?.items?.length) {
      let result = {};
      data.items.forEach((i) => {
        result = {
          ...result,
          [i.weekDay]: i,
        };
      });
      setInitialData(result);
      setDataByDay(result);
    } else {
      setInitialData({});
      setDataByDay({});
    }
    if (data && Object.keys(data).length && fromMessageCenter) {
      setFromMessageCenter(false);
    }
  }, [data]);

  useEffect(() => {
    checkIfDirty();
  }, [dataByDay]);

  useEffect(() => {
    if (serviceCenterId && serviceCenterId !== prevServiceCenterId) {
      setPrevServiceCenterId(serviceCenterId);
      fetchPlants();
    }
  }, [serviceCenterId]);

  const handlePlantChange = (value, shouldFetch = true) => {
    if (shouldFetch && value && isPlant) {
      fetchWorkingHours({ id: value });
    }
    setPlantValue(value);
    setRegions(plants?.find(p => p.id === value)?.items || []);
  };

  const handleRegionChange = (value, shouldFetch = true) => {
    if (isRegion) {
      if (value && shouldFetch) {
        fetchWorkingHours({ id: value });
      } else {
        fetchWorkingHours({ id: plantValue });
      }
    }
    setRegionValue(value);
    setFses(regions?.find(r => r.id === value)?.items || []);
  };

  const handleFseChange = (value) => {
    if (isFSE) {
      if (value) {
        fetchWorkingHours({ id: value });
      } else if (regionValue) {
        fetchWorkingHours({ id: regionValue });
      } else {
        fetchWorkingHours({ id: plantValue });
      }
    }
    setFseValue(value);
  };

  useEffect(() => {
    if (!plant && !fromMessageCenter) {
      handlePlantChange(plants[0]?.id, true);
    }
  }, [plants]);

  useEffect(() => {
    if (!plant && !fromMessageCenter) {
      handleRegionChange(regions[0]?.id, true);
    }
  }, [regions]);

  useEffect(() => {
    if (!plant && !fromMessageCenter) {
      handleFseChange(fses[0]?.id);
    }
  }, [fses]);

  useEffect(() => {
    if (plants?.length && plant) {
      setFromMessageCenter(true);
      handlePlantChange(plant, isPlant);
      if (isPlant) {
        const resetPath = history.location.pathname.replace(`/${plant}`, '');
        history.replace(resetPath);
      }
    }
    if (regions?.length && region) {
      handleRegionChange(region, isRegion);
      if (isRegion) {
        const resetPath = history.location.pathname.replace(`/${plant}/${region}`, '');
        history.replace(resetPath);
      }
    }
    if (fses?.length && fse) {
      handleFseChange(fse);
      if (isFSE) {
        const resetPath = history.location.pathname.replace(`/${plant}/${region}/${fse}`, '');
        history.replace(resetPath);
      }
    }
  }, [plants, regions, fses, plant, region, fse]);


  useEffect(() => {
    if (!plant) {
      if (plantValue && prevType && isPlant) {
        fetchWorkingHours({ id: plantValue });
      } else if (isRegion) {
        if (regionValue) {
          fetchWorkingHours({ id: regionValue });
        } else if (plantValue) {
          fetchWorkingHours({ id: plantValue });
        }
      } else if (isFSE) {
        if (fseValue) {
          fetchWorkingHours({ id: fseValue });
        } else if (regionValue) {
          fetchWorkingHours({ id: regionValue });
        } else if (plantValue) {
          fetchWorkingHours({ id: plantValue });
        }
      }
      if (prevType &&
        prevType !== settingsConfig.types.plant &&
        isPlant) {
        handleRegionChange(regions[0]?.id);
      } else if (prevType &&
        prevType !== settingsConfig.types.region &&
        isRegion) {
        handleFseChange(fses[0]?.id);
      }
      setPrevType(type);
    }
  }, [type]);

  const onSwitchChange = (day, checked) => {
    setDataByDay(state => ({
      ...state,
      [day]: {
        ...state[day],
        isWorkingDay: checked,
      },
    }));
  };

  const onRadioChange = (day) => {
    const prevStart = Object.keys(dataByDay)?.find(key => dataByDay[key].startOfWeek);
    if (prevStart || prevStart === '0') {
      setDataByDay(state => ({
        ...state,
        [prevStart]: {
          ...dataByDay[prevStart],
          startOfWeek: false,
        },
        [day]: {
          ...state[day],
          startOfWeek: true,
        },
      }));
    } else {
      setDataByDay(state => ({
        ...state,
        [day]: {
          ...state[day],
          startOfWeek: true,
        },
      }));
    }
  };


  const onTimeChange = (day, time, isStart) => {
    setDataByDay(state => ({
      ...state,
      [day]: {
        ...state[day],
        start: isStart ? moment(time, 'HH:mm').format('HH:mm:ss') : state[day].start,
        end: !isStart ? moment(time, 'HH:mm').format('HH:mm:ss') : state[day].end,
      },
    }));
  };

  const checkDisabled = () => {
    if (
      (isPlant && !plants.length) ||
      (isRegion && !regions.length) ||
      (isFSE && !fses.length)
    ) {
      return true;
    }
    return false;
  };

  const getIndicator = (item) => {
    const itemInfo = multiStatusInfo?.find(i => i.id === item.id);
    const prevItemInfo = prevMultiStatusInfo?.find(i => i.id === item.id);
    if (
      itemInfo?.operationInfo?.errors?.length ||
      itemInfo?.operationInfo?.isStale ||
      itemInfo?.operationInfo?.isTimedOut
    ) {
      return <Status>
        <ErrorIcon type='exclamation-circle' />
        <StatusText><FormattedMessage id='planningBoard.blocks.ERROR' /></StatusText>
        <StatusText>(<FormattedMessage id='planningBoard.blocks.RETRY' />)</StatusText>
      </Status>;
    } else if (itemInfo &&
      Object.keys(itemInfo).length &&
      itemInfo?.operationInfo?.status !== publishSteps.done.status) {
      return <Status>
        <LoadingIcon />
        <StatusText><FormattedMessage id='planningBoard.blocks.PUBLISHING' /></StatusText>
      </Status>;
    } else if (
      (
        prevItemInfo?.operationInfo?.status >= publishSteps.sending.status &&
        prevItemInfo?.operationInfo?.status < publishSteps.done.status
      ) &&
      itemInfo?.operationInfo?.status === publishSteps.done.status
    ) {
      if (!successIconIds?.find(i => i === item.id)) {
        setSuccessIconIds(ids => [...ids, item.id]);
        setTimeout(() => {
          setSuccessIconIds(ids => ids?.filter(i => i !== item.id));
        }, 5000);
      }
    } else if (successIconIds?.find(i => i === item.id)) {
      return <Status>
        <SuccessIcon />
        <StatusText><FormattedMessage id='planningBoard.blocks.PUBLISHED' /></StatusText>
      </Status>;
    }
    return null;
  };

  const getDisabledTimeSlots = (dayItem) => {
    const hours = moment(dayItem.end, 'HH:mm:ss').hours();
    const minutes = moment(dayItem.end, 'HH:mm:ss').minutes();
    const hoursArray = minutes === 0 ?
      Array.from({ length: 24 - hours }, (v, i) => hours + i) :
      Array.from({ length: 23 - hours }, (v, i) => hours + i + 1);
    return {
      disabledHours: () => hoursArray,
      disabledMinutes: hour =>
        hour === hours ?
          Array.from({ length: (60 - minutes) / 15 }, (v, i) => 60 - ((i + 1) * 15)) :
          [],
    };
  };

  const renderRows = () => {
    const array = data.items?.sort((a, b) => a.weekDay - b.weekDay);
    const startOfWeekDefault = 1;
    const sortedArray = cloneDeep(array);
    array.forEach((el) => {
      if (el.weekDay < startOfWeekDefault) {
        sortedArray.shift();
        sortedArray.push(array[el.weekDay]);
      }
    });

    return sortedArray?.map((item) => {
      const disabled = checkDisabled() || isRestoring;
      const dayItem = dataByDay[item.weekDay];
      const weekDay = moment().day(dayItem.weekDay).locale('en')
        .format('dddd');
      return (
        <StyledRow key={dayItem.weekDay} data-test-id={`workinghours-plant-day-${weekDay}`}>
          <Col span={4}>
            {intl.formatMessage({
              // eslint-disable-next-line
              id: `planningBoard.days.${weekDay.toUpperCase()}`,
            })}
          </Col>
          {isPlant ?
            <Col span={2}>
              {dayItem.weekDay === 0 || dayItem.weekDay === 1 ?
                <Radio
                  value={dayItem.weekDay}
                  onChange={() => onRadioChange(dayItem.weekDay)}
                  checked={dayItem.startOfWeek}
                  data-test-id={`workinghours-week-start-${weekDay}`}
                /> :
                null}
            </Col> :
            null
          }
          <Col span={2}>
            <Switch
              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
              checked={dayItem.isWorkingDay}
              onChange={checked => onSwitchChange(dayItem.weekDay, checked)}
              disabled={loading || disabled}
              data-test-id={`workinghours-is-working-day-toggle-${weekDay}`}
            />
          </Col>
          <Col span={4}>
            <StyledTimePicker
              value={moment(dayItem.start, 'HH:mm:ss')}
              allowClear={false}
              showSecond={false}
              format='HH:mm'
              minuteStep={15}
              onChange={(value, time) => onTimeChange(dayItem.weekDay, time, true)}
              disabled={loading || disabled}
              disabledTime={getDisabledTimeSlots}
              data-test-id={`workinghours-working-hours-start-picker-${weekDay}`}
            />
          </Col>
          <Col span={4}>
            <StyledTimePicker
              value={moment(dayItem.end, 'HH:mm:ss')}
              allowClear={false}
              showSecond={false}
              format='HH:mm'
              minuteStep={15}
              onChange={(value, time) => onTimeChange(dayItem.weekDay, time, false)}
              disabled={loading || disabled}
              disabledTime={() => ({
                disabledHours: () =>
                  Array.from({ length: moment(dayItem.start, 'HH:mm:ss').hours() }, (v, i) => i),
              })}
              data-test-id={`workinghours-working-hours-end-picker-${weekDay}`}
            />
          </Col>
          <Col span={isPlant ? 8 : 10}>
            {getIndicator(item)}
          </Col>
        </StyledRow>
      );
    });
  };

  const updateData = () => {
    const items = Object.values(dataByDay);
    let id = '';
    if (isPlant) {
      id = plantValue;
    } else if (isRegion) {
      if (regionValue) {
        id = regionValue;
      } else {
        id = plantValue;
      }
    } else if (isFSE) {
      if (fseValue) {
        id = fseValue;
      } else if (regionValue) {
        id = regionValue;
      } else {
        id = plantValue;
      }
    }
    updateWorkingHours({
      id,
      unlock: !!multiStatusInfo?.find(i =>
        i.operationInfo?.errors?.length ||
        i.operationInfo?.isStale ||
        i.operationInfo?.isTimedOut,
      ),
      body: {
        ...data,
        items,
      },
    });
  };

  const restoreData = () => {
    let id = '';
    if (isPlant) {
      id = plantValue;
    } else if (isRegion) {
      id = regionValue;
    } else if (isFSE) {
      id = fseValue;
    }
    restoreWorkingHours({
      id,
      unlock: !!multiStatusInfo?.find(i =>
        i.operationInfo?.errors?.length ||
        i.operationInfo?.isStale ||
        i.operationInfo?.isTimedOut,
      ),
    });
  };

  const getWarningMessage = () => {
    if (
      !data?.items?.length &&
      (isRegion || isFSE)
    ) {
      return (
        <StyledWarning ffrn>
          <WarningIcon type='info-circle' />
          <WarningText>
            {intl.formatMessage({ id: 'workingHours.DEFINE_PLANT' })}
          </WarningText>
        </StyledWarning>
      );
    } else if (data?.isMerged) {
      return (
        <>
          <StyledWarning ffrn>
            <WarningIcon type='info-circle' />
            <WarningText>{data?.mergedWorkingHoursMessage}</WarningText>
          </StyledWarning>
          <WarningList>
            {data?.mergedWorkingHoursFrom?.map(wh =>
              <WarningItem key={wh}>
                <WarningText>{wh}</WarningText>
              </WarningItem>,
            )}
          </WarningList>
        </>
      );
    } else if (data?.isDifferentFromParent) {
      return (
        <StyledWarning ffrn>
          <WarningIcon type='info-circle' />
          <WarningText>{data?.differentFromParentMessage}</WarningText>
          <RestoreButton
            light={true}
            type='secondary'
            onClick={restoreData}
            disabled={loading || isRestoring}
          >
            {intl.formatMessage({ id: 'workingHours.RESTORE' })}
          </RestoreButton>
        </StyledWarning>
      );
    }
    return null;
  };

  // When there are no working hours and the users wants to define them,
  // set the working hours to the default
  const openDefaultWorkingHours = () => {
    setDefaultWorkingHours({
      resourceId: plantValue,
    });
  };

  const renderButton = () => {
    const disabled = checkDisabled() || isRestoring;
    if (
      !loading &&
      isPlant &&
      !data?.items?.length
    ) {
      return (
        <StyledSaveButton
          onClick={openDefaultWorkingHours}
          disabled={loading || disabled}
          type='secondary'
        >
          {
            intl.formatMessage({ id: 'workingHours.DEFINE_WORKING_HOURS' })
          }
        </StyledSaveButton>
      );
    } else if (data?.items?.length) {
      return (
        <>
          <UndoButton
            onClick={() => setDataByDay(initialData)}
            disabled={!data?.isDefault && (!isDirty || loading || disabled)}
            data-test-id='working-hours-btn-undo-changes'
          >
            {
              intl.formatMessage({ id: 'workingHours.UNDO_CHANGES' })
            }
          </UndoButton>
          <StyledSaveButton
            onClick={updateData}
            disabled={!data?.isDefault && (!isDirty || loading || disabled)}
            type='secondary'
            data-test-id='working-hours-btn-save-changes'
          >
            {
              intl.formatMessage({ id: 'workingHours.SAVE_CHANGES' })
            }
          </StyledSaveButton>
        </>
      );
    }
    return null;
  };

  const serviceCenterToggleStyle = `
    width: 250px;
    margin-right: 60px;
  `;


  return (
    <>
      <ErrorHandler type='notification' error={error} />
      <TypesMenu
        items={[
          {
            title: intl.formatMessage({ id: 'holidays.PLANT' }),
            type: settingsConfig.types.plant,
          },
          {
            title: intl.formatMessage({ id: 'holidays.REGION' }),
            type: settingsConfig.types.region,
          },
          {
            title: intl.formatMessage({ id: 'holidays.FSE' }),
            type: settingsConfig.types.fse,
          },
        ]}
      />
      <>
        <StyledDiv>
          <Flex ffrn>
            <DropdownFlex ffcn>
              <StyledLabel>
                {intl.formatMessage({ id: 'holidays.CUSTOMER_CENTER' })}
              </StyledLabel>
              <ServiceCenterToggle
                needsRedirect={false}
                extraStyles={serviceCenterToggleStyle}
                onlyAdmin={true}
                disabled={loading}
              />
            </DropdownFlex>
            <DropdownFlex ffcn>
              <StyledLabel>
                {intl.formatMessage({ id: 'holidays.PLANT' })}
              </StyledLabel>
              <StyledDropdown
                placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
                onChange={v => handlePlantChange(v, true)}
                values={plants?.length > 0 ? plants : []}
                disabled={!plants || plants?.length === 0 || loading}
                valueLabel='name'
                valueKey='id'
                value={plantValue}
              />
            </DropdownFlex>
            {isRegion || isFSE ?
              <DropdownFlex ffcn>
                <StyledLabel>
                  {intl.formatMessage({ id: 'holidays.REGION' })}
                </StyledLabel>
                <StyledDropdown
                  placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
                  onChange={v => handleRegionChange(v, true)}
                  values={regions?.length > 0 ? regions : []}
                  disabled={!regions || regions?.length === 0 || loading}
                  valueLabel='name'
                  valueKey='id'
                  value={regionValue}
                />
              </DropdownFlex> :
              null
            }
            {isFSE ?
              <DropdownFlex ffcn>
                <StyledLabel>
                  {intl.formatMessage({ id: 'holidays.FSE' })}
                </StyledLabel>
                <StyledDropdown
                  placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
                  onChange={handleFseChange}
                  values={fses?.length > 0 ? fses : []}
                  disabled={!fses || fses?.length === 0 || loading}
                  valueLabel='name'
                  valueKey='id'
                  value={fseValue}
                />
              </DropdownFlex> :
              null
            }
          </Flex>
          {!loading ?
            <Warning>
              {getWarningMessage()}
            </Warning> :
            null
          }
          {isFetching ?
            <Spinner /> :
            null
          }
          <StyledGrid>
            {data && dataByDay && Object.keys(dataByDay).length && Object.keys(data).length ?
              <>
                <StyledRow>
                  <Col span={4}>
                    <StyledLabel>
                      {intl.formatMessage({ id: 'workingHours.WEEKDAY' })}
                    </StyledLabel>
                  </Col>
                  {isPlant ?
                    <Col span={2}>
                      <StyledLabel>
                        {intl.formatMessage({ id: 'workingHours.WEEK_START' })}
                      </StyledLabel>
                    </Col> :
                    null
                  }
                  <Col span={2}>
                    <StyledLabel>
                      {intl.formatMessage({ id: 'workingHours.WORKING_DAY' })}
                    </StyledLabel>
                  </Col>
                  <Col span={4}>
                    <StyledLabel>
                      {intl.formatMessage({ id: 'workingHours.WORKING_HOURS_START' })}
                    </StyledLabel>
                  </Col>
                  <Col span={4}>
                    <StyledLabel>
                      {intl.formatMessage({ id: 'workingHours.WORKING_HOURS_END' })}
                    </StyledLabel>
                  </Col>
                  <Col span={isPlant ? 8 : 10} />
                </StyledRow>
                {renderRows()}
              </> :
              null
            }
          </StyledGrid>
          {renderButton()}
        </StyledDiv>
      </>
    </>
  );
};

WorkingHours.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  fetchPlants: PropTypes.func,
  plants: PropTypes.array,
  serviceCenterId: PropTypes.string,
  intl: PropTypes.object,
  error: PropTypes.object,
  isLoadingPlants: PropTypes.bool,
  data: PropTypes.object,
  fetchWorkingHours: PropTypes.func,
  updateWorkingHours: PropTypes.func,
  isLoading: PropTypes.bool,
  updateSuccess: PropTypes.bool,
  isFetching: PropTypes.bool,
  setDefaultWorkingHours: PropTypes.func,
  restoreWorkingHours: PropTypes.func,
  isRestoring: PropTypes.bool,
};

export default injectIntl(withRouter(WorkingHours));
